import { api_ } from '@shared/plugIn/axios';
import { LowerRole } from '@shared/types';

import { GiftStoreRole } from '../giftOrder/giftOrder.interface';
import {
  DeliveryChannel,
  GiftCoupon,
  GiftCouponForStore,
  GiftCouponRedeemStatus,
  GiftCouponRequestDto,
  GiftCouponSaveRecipientDto,
  GiftCouponShareStatus,
  GiftCouponStatistics,
  VerificationConfirmDto,
  VerificationConfirmResponse,
  VerificationSendDto,
  VerificationSendResponse,
} from './giftCoupon.interface';

// 공유 처리 (ShareStatus: NotShared → Shared)
export const shareCoupon = async (
  id: string,
  role: GiftStoreRole,
): Promise<GiftCouponForStore> => {
  const response = await api_.post<GiftCouponForStore>(
    `/${role}/gift-coupon/share/${id}`,
  );
  return response.data;
};

// 공유 취소 (ShareStatus: Shared → NotShared)
export const unshareCoupon = async (
  id: string,
  role: GiftStoreRole,
): Promise<GiftCouponForStore> => {
  const response = await api_.post<GiftCouponForStore>(
    `/${role}/gift-coupon/unshare/${id}`,
  );
  return response.data;
};

// 전달 확인 (confirmedAt 기록 + memo optional)
export const confirmCoupon = async (
  id: string,
  role: GiftStoreRole,
  memo?: string,
): Promise<GiftCouponForStore> => {
  const response = await api_.post<GiftCouponForStore>(
    `/${role}/gift-coupon/confirm-share/${id}`,
    memo ? { memo } : {},
  );
  return response.data;
};

// 조회 처리 (ShareStatus: Shared → Viewed)
export const viewCoupon = async (
  id: string,
  role: GiftStoreRole = LowerRole.Public,
): Promise<GiftCouponForStore> => {
  const response = await api_.post<GiftCouponForStore>(`/${role}/gift-coupon/view/${id}`);
  return response.data;
};

// 교환 신청 (RedeemStatus: Available → Requested)
export const requestCouponRedemption = async (
  id: string,
  dto: GiftCouponRequestDto,
  role: GiftStoreRole = LowerRole.Public,
): Promise<GiftCouponForStore> => {
  const response = await api_.post<GiftCouponForStore>(
    `/${role}/gift-coupon/request/${id}`,
    dto,
  );
  return response.data;
};

// 나중에 신청하기 — 수신자 번호만 미리 저장
export const saveRecipient = async (
  id: string,
  dto: GiftCouponSaveRecipientDto,
  role: GiftStoreRole = LowerRole.Public,
): Promise<GiftCouponForStore> => {
  const response = await api_.post<GiftCouponForStore>(
    `/${role}/gift-coupon/save-recipient/${id}`,
    dto,
  );
  return response.data;
};

// 발송 처리 (RedeemStatus: Requested → Delivered)
export interface DeliverCouponDto {
  priceWhole: number;
  priceRetail: number;
  priceRebate: number;
  /** 미선택 시 빈 문자열로 전달 */
  deliveryChannel: DeliveryChannel | '';
}

export const deliverCoupons = async (
  couponIds: string[],
  dto: DeliverCouponDto,
): Promise<GiftCouponForStore[]> => {
  const response = await api_.post<GiftCouponForStore[]>(
    `/${LowerRole.Admin}/gift-coupon/deliver`,
    {
      couponIds,
      priceWhole: dto.priceWhole,
      priceRetail: dto.priceRetail,
      priceRebate: dto.priceRebate,
      deliveryChannel: dto.deliveryChannel,
    },
  );
  return response.data;
};

export const deliverCoupon = async (
  id: string,
  dto: DeliverCouponDto,
): Promise<GiftCouponForStore> => {
  const results = await deliverCoupons([id], dto);
  return results[0] as GiftCouponForStore;
};

// 발송불가 처리 (RedeemStatus: Requested → Rejected)
export const rejectCoupon = async (id: string): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, {
    redeemStatus: GiftCouponRedeemStatus.Rejected,
  });
  return response.data;
};

// 수신자 전화번호 수정 (Admin)
export const updateRecipientPhone = async (
  id: string,
  phoneNumber: string,
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, {
    'recipient.phoneNumber': phoneNumber,
  });
  return response.data;
};

// Admin에서 공유 상태를 NotShared + 교환 상태를 Available로 초기화
export const resetCouponToNotShared = async (id: string): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, {
    shareStatus: GiftCouponShareStatus.NotShared,
    redeemStatus: GiftCouponRedeemStatus.Available,
  });
  return response.data;
};

// 쿠폰 통계 조회 (Admin)
export const fetchGiftCouponStatistics = async (): Promise<GiftCouponStatistics> => {
  const response = await api_.get<GiftCouponStatistics>(
    `/${LowerRole.Admin}/gift-coupon/statistics`,
  );
  return response.data;
};

// 재발행 쿠폰 생성 (Admin)
export interface CreateAdditionalCouponsDto {
  giftOrderId: string;
  count: number;
  giftItemId?: string;
}

export const createAdditionalCoupons = async (
  dto: CreateAdditionalCouponsDto,
): Promise<GiftCouponForStore[]> => {
  const response = await api_.post<GiftCouponForStore[]>(
    `/${LowerRole.Admin}/gift-coupon/additional`,
    dto,
  );
  return response.data;
};

// 재발행 쿠폰 폐기 (Admin) - DB에서 완전 삭제
export const deleteAdditionalCoupon = async (id: string): Promise<void> => {
  await api_.delete(`/${LowerRole.Admin}/gift-coupon/additional/${id}`);
};

// 재발행 쿠폰 수신자 전화번호 저장 (Admin - 기존 patch 방식)
export const updateCouponAdditionalRecipientPhone = async (
  id: string,
  additionalRecipientPhone: string,
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, {
    additionalRecipientPhone,
  });
  return response.data;
};

// 확인번호 + 메모 함께 업데이트 (Admin)
export const updateCouponPhoneAndMemo = async (
  id: string,
  data: { additionalRecipientPhone?: string; memo?: string },
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, data);
  return response.data;
};

// 재발행 쿠폰 수신자 정보 변경 (Store - 전용 엔드포인트)
export const updateAdditionalRecipient = async (
  id: string,
  phoneNumber: string,
  role: GiftStoreRole,
  memo?: string,
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(
    `/${role}/gift-coupon/additional-recipient/${id}`,
    { phoneNumber, ...(memo ? { memo } : {}) },
  );
  return response.data;
};

// 쿠폰 폐기만 (재발행 없이) (Admin)
export interface DisposeCouponsDto {
  couponIds: string[];
  reason?: string;
}

export const disposeCoupons = async (dto: DisposeCouponsDto): Promise<GiftCoupon[]> => {
  const response = await api_.post<GiftCoupon[]>(
    `/${LowerRole.Admin}/gift-coupon/dispose`,
    dto,
  );
  return response.data;
};

// 쿠폰 폐기 + 재발급 (Admin)
export interface DisposeAndReissueCouponsDto {
  couponIds: string[];
  reason?: string;
}

export interface DisposeAndReissueResult {
  disposed: GiftCoupon[];
  reissued: GiftCoupon[];
}

export const disposeAndReissueCoupons = async (
  dto: DisposeAndReissueCouponsDto,
): Promise<DisposeAndReissueResult> => {
  const response = await api_.post<DisposeAndReissueResult>(
    `/${LowerRole.Admin}/gift-coupon/dispose-and-reissue`,
    dto,
  );
  return response.data;
};

// additional → reissued 승격 (Admin)
export const promoteToReissued = async (id: string): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(
    `/${LowerRole.Admin}/gift-coupon/${id}/promote-to-reissued`,
  );
  return response.data;
};

// 발송매체(deliveryChannel)만 수정 (Admin)
export const updateCouponDeliveryChannel = async (
  id: string,
  deliveryChannel: DeliveryChannel | '',
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, {
    'gifticon.deliveryChannel': deliveryChannel,
  });
  return response.data;
};

// 발송일시(deliveredAt)만 수정 (Admin)
export const updateCouponDeliveredAt = async (
  id: string,
  deliveredAt: string,
): Promise<GiftCouponForStore> => {
  const response = await api_.patch<GiftCouponForStore>(`/admin/gift-coupon/${id}`, {
    'gifticon.deliveredAt': deliveredAt,
  });
  return response.data;
};

// 일반 환불 (미발송 쿠폰)
export interface RefundCouponDto {
  couponId: string;
  amount: number;
}

export const refundCoupon = async (dto: RefundCouponDto): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(`/admin/gift-coupon/refund`, dto);
  return response.data;
};

// 발송 후 환불 (이미 발송된 쿠폰)
export interface RefundAfterDeliveryDto {
  couponId: string;
  amount: number;
}

export const refundAfterDelivery = async (
  dto: RefundAfterDeliveryDto,
): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(
    `/admin/gift-coupon/refund-after-delivery`,
    dto,
  );
  return response.data;
};

// 환불/연장 Issue 발행 (Admin → 고객에게 신청 링크 허용)
export interface IssueCouponDto {
  couponId: string;
  type: 'refund' | 'renewal';
}

export const issueCoupon = async (dto: IssueCouponDto): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(`/admin/gift-coupon/issue`, dto);
  return response.data;
};

// Issue 취소 (Admin)
export const cancelIssueCoupon = async (id: string): Promise<GiftCoupon> => {
  const response = await api_.delete<GiftCoupon>(`/admin/gift-coupon/issue/${id}`);
  return response.data;
};

// Issued 목록 조회 (Admin)
export const findIssuedCoupons = async (): Promise<GiftCoupon[]> => {
  const response = await api_.get<GiftCoupon[]>(`/admin/gift-coupon/issued`);
  return response.data;
};

// 환불 승인 (Admin)
export interface ApproveRefundDto {
  couponId: string;
  amount: number;
}

export const approveRefund = async (dto: ApproveRefundDto): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(`/admin/gift-coupon/approve-refund`, dto);
  return response.data;
};

// 연장 승인 (Admin)
export interface ApproveRenewalDto {
  couponId: string;
  priceWhole: number;
  priceRetail: number;
  priceRebate: number;
  deliveryChannel: string;
}

export const approveRenewal = async (dto: ApproveRenewalDto): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(`/admin/gift-coupon/approve-renewal`, dto);
  return response.data;
};

// Public: 환불 신청 (고객이 링크에서 작성)
export interface RefundRequestDto {
  phoneNumber: string;
  name: string;
  bank: string;
  accountNumber: string;
  accountHolder: string;
  signatureImage: string;
}

export const requestRefund = async (
  couponId: string,
  dto: RefundRequestDto,
): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(
    `/${LowerRole.Public}/gift-coupon/refund-request/${couponId}`,
    dto,
  );
  return response.data;
};

// 연장쿠폰 생성
export interface CreateRenewalCouponDto {
  originalCouponId: string;
  recipientPhoneNumber?: string;
}

export const createRenewalCoupon = async (
  dto: CreateRenewalCouponDto,
): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(`/admin/gift-coupon/renewal`, dto);
  return response.data;
};

// 연장쿠폰 삭제
export const deleteRenewalCoupon = async (id: string): Promise<void> => {
  await api_.delete(`/admin/gift-coupon/renewal/${id}`);
};

// Public: 연장쿠폰 신청 (고객이 링크에서 작성)
export interface RenewalRequestDto {
  phoneNumber: string;
  name: string;
  signatureImage: string;
}

export const requestRenewal = async (
  couponId: string,
  dto: RenewalRequestDto,
): Promise<GiftCoupon> => {
  const response = await api_.post<GiftCoupon>(
    `/${LowerRole.Public}/gift-coupon/renewal-request/${couponId}`,
    dto,
  );
  return response.data;
};

// 번호 인증 코드 발송
export const sendVerificationCode = async (
  dto: VerificationSendDto,
): Promise<VerificationSendResponse> => {
  const response = await api_.post<VerificationSendResponse>(
    `/${LowerRole.Public}/gift-coupon/verification/send`,
    dto,
  );
  return response.data;
};

// 번호 인증 코드 확인
export const confirmVerificationCode = async (
  dto: VerificationConfirmDto,
): Promise<VerificationConfirmResponse> => {
  const response = await api_.post<VerificationConfirmResponse>(
    `/${LowerRole.Public}/gift-coupon/verification/confirm`,
    dto,
  );
  return response.data;
};
